// App.js
import React, { useEffect, useRef } from "react";
import {  Routes, Route, Navigate } from "react-router-dom";
import { AppBar, Toolbar, Typography, Box, Button, Chip, Tooltip } from "@mui/material";

import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import Tasks from "./pages/Tasks";
import Entities from "./pages/Entities";
import UserManagement from "./pages/UserManagement";
import Login from "./auth/Login";
import Signup from "./auth/Signup";
import LandingPage from "./pages/LandingPage";
import Unauthorized from "./pages/Unauthorized";
import SuperDashboard from "./pages/SuperDashboard";
import TeamManagerPage from "./pages/TeamManagerPage";
import TeamMemberPage from "./pages/TeamMemberPage";
import ActivityLog from "./pages/ActivityLog";
import Disclaimer from "./components/Disclaimer";
import UserAgreement from "./components/UserAgreement";
import WorkPulsePolicy from "./components/WorkPulsePolicy";

import ProjectInformationPage from "./pages/ProjectInformationPage";
import UserTaskSummaryPage from "./pages/UserTaskSummaryPage";

import { AuthProvider, useAuth } from "./authContext";
import ProtectedRoute from "./ProtectedRoute";
import { Link as RouterLink } from "react-router-dom";


import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";

import { useNavigate } from "react-router-dom";


import LoginActivity from "./pages/LoginActivity";

import { ThemeProvider, CssBaseline } from "@mui/material";
import { appTheme } from "./theme/appTheme"; // adjust path if your folder structure is different
import { BrowserRouter as Router } from "react-router-dom";

import Timesheet from "./pages/Timesheet";
 import TimesheetInsights from "./pages/TimesheetInsights";

// import Attendance from "./pages/Attendance";
import ProjectManagement from "./pages/ProjectManagement";

import Profile from "./pages/Profile";

import NotificationsPanel from "./components/NotificationsPanel";

import Contact from "./pages/Contact";

import { useLocation } from "react-router-dom";
import { analytics } from "./firebase/firebaseConfig";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";

import ResourcesPublic from "./pages/ResourcesPublic";
import ResourceView from "./pages/ResourceView";
import ResourcesAdmin from "./pages/ResourcesAdmin";


import ReportExportPage from './ReportExportPage';


// Owner-only route that waits for auth to load
function OwnerRoute({ children }) {
  const { loading, platformRole } = useAuth();
  if (loading) {
    return <div style={{ padding: 32 }}>Loading…</div>;
  }
  const isOwner = (platformRole ?? "").toLowerCase() === "owner";
  return isOwner ? children : <Navigate to="/unauthorized" replace />;
}

function GAListener() {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (!analytics) return;

    // Set user_id on Analytics (optional but recommended)
    if (user?.uid) {
      setUserId(analytics, user.uid);
      // optional: set useful user properties
      const company = user?.companyName || user?.company?.name || "Company";
      setUserProperties(analytics, { company_name: company });
    }

    // Log a page_view on every route change
    logEvent(analytics, "page_view", {
      page_path: location.pathname + location.search,
      page_title: document.title || "WorkPulse",
    });
  }, [location, user]);

  return null;
}


  

const AppLayout = () => {
  const { user, platformRole, refreshClaims } = useAuth();
 const companyLabel = user?.companyName || user?.company?.name || "Company";
 
  const navigate = useNavigate();

    


  // define it here so it exists for your header/links/buttons
  const isPlatformOwner = (platformRole ?? "").toLowerCase() === "owner";


const idleTimer = useRef(null);

  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(async () => {
        await signOut(getAuth());
        navigate("/login");
      }, 60 * 60 * 1000); // 1 hour = 3600000 ms
    };

    // List of events considered as activity
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer(); // start timer immediately

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [user, navigate]);

 
const location = useLocation(); // NEW: route-aware context for the chatbot



// --- WorkPulse Context: expose for chatbot (BEGIN)
useEffect(() => {
  // Resolve the in-app role (fallback to Member).
  // If you store role differently, tweak these lines.
  const appRole =
    user?.role ||
    user?.claims?.role ||
    (user?.isAdmin ? "Admin" : user?.isManager ? "Manager" : "Member");

  const companyName = user?.companyName || user?.company?.name || "Company";
  const companyId   = user?.companyId   || user?.company?.id   || null;

  const userName =
    user?.displayName ||
    user?.name ||
    (user?.email ? user.email.split("@")[0] : "User");

  // Make it available to /wp-chatbot.js
  window.WorkPulseContext = {
    role: appRole,
    companyId,
    companyName,
    userName,
    route: location.pathname + location.search
  };
}, [user, location]);
// --- WorkPulse Context: expose for chatbot (END)


// Load chatbot only when a user is logged in, and attach idToken
useEffect(() => {
  // Remove any prior widget (helps on logout/login or hot reloads)
  document.querySelectorAll('script[data-wp-chatbot]').forEach(s => s.remove());

  if (!user) {
    // also clear context on logout
    window.WorkPulseContext = undefined;
    return;
  }

  // enrich the context with a fresh ID token so the server can verify auth
  (async () => {
    try {
      const auth = getAuth();
      const idToken = await auth.currentUser?.getIdToken();

      // merge into the context you already expose elsewhere
      window.WorkPulseContext = {
        ...(window.WorkPulseContext || {}),
        userId: user.uid,
        email: user.email || "",
        viewerName: user.displayName || "",
        idToken,
      };

      // inject the widget after login
      const s = document.createElement("script");
      s.src = "/wp-chatbot.js?v=" + Date.now(); // cache-bust on reloads
      s.async = true;
      s.defer = true;
      s.setAttribute("data-wp-chatbot", "1");
      document.body.appendChild(s);
    } catch (e) {
      console.error("Failed to inject chatbot:", e);
    }
  })();
}, [user]);



  // Force-reload ID token so new custom claims are picked up immediately.
 const handleRefreshAccess = async () => {
   await refreshClaims();        // <-- comes from useAuth()
   window.location.reload();     // re-evaluate guards/UI with the fresh token
 };





  // Log out and go to /login
  const handleLogout = async () => {
  // hide widget immediately
  if (window.__WP_CHATBOT_CLEANUP__) {
    try { window.__WP_CHATBOT_CLEANUP__(); } catch {}
  }
  document
    .querySelectorAll('#wpchatbot-panel,#wpchatbot-btn,[data-wp-chatbot-style]')
    .forEach(n => n.remove());

  await signOut(getAuth());
  navigate("/login");
};


  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header */}
<AppBar position="static" sx={{ backgroundColor: "#F4F4F4", boxShadow: 2 }}>
   <Toolbar sx={{ justifyContent: "space-between", pl: { xs: "56px", sm: "64px" }}}>
     
     <Typography
       variant="h6"
       sx={{ cursor: "pointer", color: "#2979FF" }}
       onClick={() => navigate("/dashboard")}
     >
       WorkPulse.in
     </Typography>

    {!user && (
      <Button variant="outlined" onClick={() => navigate("/resources")}>
        Resources
      </Button>
    )}


     {user && (
       <Box sx={{ display: "flex", gap: 1 }}>
         <Button size="small" onClick={handleRefreshAccess}>
           Refresh page
         </Button>
         <Button size="small" onClick={handleLogout}>
           Log out
         </Button>

<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}></Box>
<Tooltip title="Logged-in company">
       <Chip
         size="medium"
         variant="outlined"
         label={companyLabel}
         sx={{ fontWeight: 700 }}
       />
     </Tooltip>

{isPlatformOwner && (
  <Button size="small" onClick={() => navigate("/super")}>WorkPulseIN DB</Button>
)}

{isPlatformOwner && (
  <Button size="small" onClick={() => navigate("/resources-admin")}>
    Resources Admin
  </Button>
)}

       </Box>
     )}
   </Toolbar>
 </AppBar>


{/* ✅ Analytics page-view tracker */}
<GAListener />


      {/* Main Layout */}
      <Box sx={{ display: "flex", flex: 1 }}>
        {user && <Sidebar />}
        <Box component="main" sx={{ flexGrow: 1, p: 3, flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start" }}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/project-management" element={<ProjectManagement />} />

            <Route path="/login-activity" element={<LoginActivity />} />

            <Route path="/contact" element={<Contact />} />
            <Route path="/resources" element={<ResourcesPublic />} />
            <Route path="/resources/:slug" element={<ResourceView />} />


            <Route
  path="/super"
  element={
    <OwnerRoute>
      <SuperDashboard />
    </OwnerRoute>
  }
/>

<Route
  path="/resources-admin"
  element={
    <OwnerRoute>
      <ResourcesAdmin />
    </OwnerRoute>
  }
/>

<Route
    path="/notifications"
    element={
      <ProtectedRoute allowedRoles={["Admin","Manager","Member"]}>
        <NotificationsPanel variant="page" />
      </ProtectedRoute>
    }
  />

<Route
  path="/reports-export"
  element={
    <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
      <ReportExportPage />
    </ProtectedRoute>
  }
/>


            {/* Dashboard (landing for signed-in users) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* NEW: Split pages */}
            <Route
              path="/projects-info"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <ProjectInformationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-task-summary"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <UserTaskSummaryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/timesheet"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <Timesheet />
                </ProtectedRoute>
              }
            />

 <Route path="/timesheet-insights" element={<ProtectedRoute><TimesheetInsights /></ProtectedRoute>} />



            {/* Existing protected pages */}
            <Route
              path="/projects"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager"]}>
                  <Projects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <Tasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/entities"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager"]}>
                  <Entities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager"]}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <SuperDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager"]}>
                  <TeamManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/member"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <TeamMemberPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity-log"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <ActivityLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/disclaimer"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <Disclaimer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/UserAgreement"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <UserAgreement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/WorkPulsePolicy"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Manager", "Member"]}>
                  <WorkPulsePolicy />
                </ProtectedRoute>
              }
            />


<Route
  path="/profile"
  element={
    <ProtectedRoute allowedRoles={["Admin"]}>
      <Profile />
    </ProtectedRoute>
  }
/>

          </Routes>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          textAlign: "center",
          py: 2,
          fontSize: "0.875rem",
          color: "#888",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="body1" sx={{ mb: 3 }}>
          {new Date().getFullYear()} WorkPulse.in |{" "}
          <RouterLink to="/disclaimer" style={{ color: "#2979FF", textDecoration: "none" }}>
            Disclaimer
          </RouterLink>
          {" | "}
          <RouterLink to="/UserAgreement" style={{ color: "#2979FF", textDecoration: "none" }}>
            User Agreement
          </RouterLink>
          {" | "}
          <RouterLink to="/WorkPulsePolicy" style={{ color: "#2979FF", textDecoration: "none" }}>
            WorkPulse Policy
          </RouterLink>
        </Typography>
      </Box>
    </Box>
  );
};


function LoginAudit() {
  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    let ran = false; // avoid double-writes in a single app session
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user || ran) return;
      ran = true;

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          await setDoc(
            ref,
            {
              previousLoginAt: null,
              lastLoginAt: serverTimestamp(),
              
              email: user.email || "",
            },
            { merge: true }
          );
        } else {
          const prev = snap.data()?.lastLoginAt || null;
          await updateDoc(ref, {
            previousLoginAt: prev || null,
            lastLoginAt: serverTimestamp(),
          });
        }
      } catch (err) {
        console.error("Login audit write failed:", err);
      }
    });

    return () => unsub();
  }, []);

  return null;
}


function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <LoginAudit />
        <AppLayout />
      </ThemeProvider>
    </AuthProvider>
  );
}



export default App;
